import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Star,
  Clock,
  Search,
  Download,
  MoreHorizontal,
  Edit,
  Eye,
  Phone,
  MapPin,
  Calendar,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  Users,
  CheckCircle,
  XCircle,
  Plus,
} from 'lucide-react'
import { format } from 'date-fns'
import ApiService from '../services/api'
import toast from 'react-hot-toast'
import AddEditPanditModal from '../components/modals/AddEditPanditModal'

function Pandits() {
  const navigate = useNavigate()
  const [pandits, setPandits] = useState([])
  const [stats, setStats] = useState({
    totalPandits: 0,
    verifiedPandits: 0,
    unverifiedPandits: 0,
    featuredPandits: 0,
    newPanditsThisMonth: 0,
    panditsByState: {},
    panditsByExperience: {}
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterVerification, setFilterVerification] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  })

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingPandit, setEditingPandit] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)
  const [services, setServices] = useState([])

  // Action loading states
  const [verifyingPandits, setVerifyingPandits] = useState(new Set())
  const [featuringPandits, setFeaturingPandits] = useState(new Set())

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, filterStatus, filterVerification])

  // Load pandit stats and services only once on component mount
  useEffect(() => {
    fetchPanditStats()
    fetchServices()
  }, [])

  // Load pandits data when filters change
  useEffect(() => {
    const abortController = new AbortController()

    const fetchPanditsWithAbort = async () => {
      try {
        setLoading(true)
        setError(null)
        const params = new URLSearchParams({
          page: currentPage.toString(),
          limit: itemsPerPage.toString(),
          ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
          ...(filterVerification !== 'all' && { isVerified: filterVerification === 'verified' }),
          ...(filterStatus !== 'all' && { featuredPandit: filterStatus === 'featured' }),
          sortBy: 'createdAt',
          sortOrder: 'desc'
        })

        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/pandits?${params}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('admin_token') && {
              Authorization: `Bearer ${localStorage.getItem('admin_token')}`
            }),
          },
          signal: abortController.signal
        })

        if (abortController.signal.aborted) return

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.message || `HTTP error! status: ${response.status}`)
        }

        setPandits(data.data || [])
        setPagination(data.pagination || {})
      } catch (error) {
        if (error.name === 'AbortError') {
          return
        }
        console.error('Error fetching pandits:', error)
        setError(error.message || 'Failed to load pandits')
        toast.error('Failed to load pandits')
        setPandits([])
        setPagination({
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        })
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    fetchPanditsWithAbort()

    return () => {
      abortController.abort()
    }
  }, [currentPage, debouncedSearchTerm, filterStatus, filterVerification, itemsPerPage])

  const fetchPanditStats = async () => {
    try {
      const response = await ApiService.getPanditStats()
      setStats(response.data || {})
    } catch (error) {
      console.error('Error fetching pandit stats:', error)
      toast.error('Failed to load pandit statistics')
      // Keep default empty stats
      setStats({
        totalPandits: 0,
        verifiedPandits: 0,
        unverifiedPandits: 0,
        featuredPandits: 0,
        newPanditsThisMonth: 0,
        panditsByState: {},
        panditsByExperience: {}
      })
    }
  }

  const handleVerifyPandit = async (panditId) => {
    try {
      setVerifyingPandits(prev => new Set([...prev, panditId]))
      await ApiService.verifyPandit(panditId)
      toast.success('Pandit verified successfully')
      window.location.reload()
    } catch (error) {
      console.error('Error verifying pandit:', error)
      toast.error('Failed to verify pandit')
    } finally {
      setVerifyingPandits(prev => {
        const newSet = new Set(prev)
        newSet.delete(panditId)
        return newSet
      })
    }
  }

  const handleFeaturePandit = async (panditId, featured) => {
    try {
      setFeaturingPandits(prev => new Set([...prev, panditId]))
      await ApiService.updatePandit(panditId, { featuredPandit: featured })
      toast.success(`Pandit ${featured ? 'featured' : 'unfeatured'} successfully`)
      window.location.reload()
    } catch (error) {
      console.error('Error updating pandit featured status:', error)
      toast.error('Failed to update pandit status')
    } finally {
      setFeaturingPandits(prev => {
        const newSet = new Set(prev)
        newSet.delete(panditId)
        return newSet
      })
    }
  }

  const exportPandits = () => {
    toast.success('Export functionality coming soon!')
  }

  const fetchServices = async () => {
    try {
      const response = await ApiService.getAllAdminServices({ limit: 100 })
      setServices(response.data || [])
    } catch (error) {
      console.error('Error fetching services:', error)
      // Don't show error toast for services as it's not critical
    }
  }

  const handleAddPandit = () => {
    setEditingPandit(null)
    setShowAddModal(true)
  }

  const handleEditPandit = (pandit) => {
    setEditingPandit(pandit)
    setShowAddModal(true)
  }

  const handleSavePandit = async (panditData) => {
    try {
      setModalLoading(true)

      if (editingPandit) {
        // Update existing pandit
        await ApiService.updatePandit(editingPandit.id, panditData)
        toast.success('Pandit updated successfully')
      } else {
        // Create new pandit
        await ApiService.createPanditByAdmin(panditData)
        toast.success('Pandit created successfully')
      }

      setShowAddModal(false)
      setEditingPandit(null)

      // Refresh the pandits list and stats
      window.location.reload()
    } catch (error) {
      console.error('Error saving pandit:', error)
      toast.error(error.message || 'Failed to save pandit')
    } finally {
      setModalLoading(false)
    }
  }

  const handleCloseModal = () => {
    setShowAddModal(false)
    setEditingPandit(null)
  }

  const filteredPandits = pandits

  const statCards = [
    {
      title: 'Total Pandits',
      value: stats.totalPandits,
      icon: Users,
      color: 'bg-blue-500',
      change: '+5%',
      changeType: 'positive',
      description: 'Registered pandits'
    },
    {
      title: 'Verified Pandits',
      value: stats.verifiedPandits,
      icon: UserCheck,
      color: 'bg-green-500',
      change: '+12%',
      changeType: 'positive',
      description: 'Verified and active'
    },
    {
      title: 'Featured Pandits',
      value: stats.featuredPandits,
      icon: Star,
      color: 'bg-yellow-500',
      change: stats.featuredPandits > 20 ? 'Optimal' : 'Add more',
      changeType: stats.featuredPandits > 20 ? 'neutral' : 'attention',
      description: 'Premium listings'
    },
    {
      title: 'Pending Verification',
      value: stats.unverifiedPandits,
      icon: Clock,
      color: 'bg-red-500',
      change: stats.unverifiedPandits > 50 ? 'High queue' : 'Manageable',
      changeType: stats.unverifiedPandits > 50 ? 'attention' : 'neutral',
      description: 'Awaiting review'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            Pandits Management
          </h1>
          <p className="mt-2 text-gray-600">
            Manage all pandits, verification requests, and featured listings
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={exportPandits}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <button
            onClick={handleAddPandit}
            className="inline-flex items-center px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors duration-200"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Pandit
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon
          return (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 opacity-5">
                <div className="w-full h-full bg-gradient-to-br from-orange-400 to-red-500 rounded-full transform translate-x-8 -translate-y-8"></div>
              </div>

              <div className="flex items-center justify-between relative z-10">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg shadow-sm`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <span className={`text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' :
                    stat.changeType === 'attention' ? 'text-orange-600' : 'text-gray-600'
                  }`}>
                  {stat.change}
                </span>
                <span className="text-sm text-gray-500 ml-2">status</span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search name, email, phone, city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full sm:w-64 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <select
              value={filterVerification}
              onChange={(e) => setFilterVerification(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Verification</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Status</option>
              <option value="featured">Featured</option>
              <option value="regular">Regular</option>
            </select>
          </div>

          <div className="text-sm text-gray-600">
            Showing {pagination.total ? `${(pagination.page - 1) * pagination.limit + 1} - ${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total}` : '0'} pandits
          </div>
        </div>
      </div>

      {/* Pandits Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Pandits</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading pandits...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Pandits</h3>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        ) : filteredPandits.length === 0 ? (
          <div className="p-8 text-center">
            <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No pandits found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pandit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact & Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Verification
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location & Experience
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rating & Pricing
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPandits.map((pandit) => (
                  <tr
                    key={pandit.id}
                    className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                    onClick={() => navigate(`/pandits/${pandit.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-orange-400 to-red-500 flex items-center justify-center relative">
                            <span className="text-white font-semibold text-sm">
                              {pandit.profile?.name?.charAt(0)?.toUpperCase() || pandit.user?.email?.charAt(0)?.toUpperCase()}
                            </span>
                            {pandit.featuredPandit && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                                <Star className="w-2 h-2 text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 flex items-center">
                            {pandit.profile?.name || 'No name provided'}
                            {pandit.featuredPandit && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Featured
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {pandit?.email}
                          </div>
                          {pandit.expertise?.[0] && (
                            <div className="text-xs text-orange-600 mt-1">
                              {pandit.expertise[0].service?.name}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {pandit.phone && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Phone className="w-3 h-3 mr-1" />
                            {pandit.phone}
                          </div>
                        )}
                        <div className="flex items-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            pandit.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : pandit.status === 'INACTIVE'
                              ? 'bg-gray-100 text-gray-800'
                              : pandit.status === 'PROFILE_REJECTED'
                              ? 'bg-orange-100 text-orange-800'
                              : pandit.status === 'SUSPENDED'
                              ? 'bg-red-100 text-red-800'
                              : pandit.status === 'DELETED'
                              ? 'bg-red-200 text-red-900'
                              : 'bg-gray-100 text-gray-800'
                            }`}>
                            {pandit.status || 'UNKNOWN'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          Languages: {pandit.profile?.languages?.join(', ') || 'Not specified'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {pandit.isVerified ? (
                          <div className="flex items-center text-green-600">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            <span className="text-sm font-medium">Verified</span>
                          </div>
                        ) : (
                          <div className="flex items-center text-red-600">
                            <XCircle className="w-4 h-4 mr-1" />
                            <span className="text-sm font-medium">Unverified</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-900 flex items-center">
                          <MapPin className="w-3 h-3 mr-1 text-gray-400" />
                          {pandit.profile?.city}, {pandit.profile?.state}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {pandit.profile?.experience || 0} years experience
                        </div>
                        {pandit.expertise?.[0] && (
                          <div className="text-xs text-gray-600">
                            Level: {pandit.expertise[0].experienceLevel}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {pandit.profile?.ratingAvg && (
                          <div className="flex items-center">
                            <Star className="w-3 h-3 text-yellow-400 mr-1" />
                            <span className="text-sm font-medium">{pandit.profile.ratingAvg}</span>
                            <span className="text-xs text-gray-500 ml-1">
                              ({pandit.profile.ratingCount || 0} reviews)
                            </span>
                          </div>
                        )}
                        {pandit.pricing?.[0] && (
                          <div className="text-sm text-gray-900">
                            ₹{pandit.pricing[0].price}
                          </div>
                        )}
                        {pandit.pricing?.[0]?.durationMin && (
                          <div className="text-xs text-gray-500">
                            {pandit.pricing[0].durationMin} mins
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <Calendar className="w-3 h-3 mr-1 text-gray-400" />
                        {format(new Date(pandit.createdAt), 'MMM dd, yyyy')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {format(new Date(pandit.createdAt), 'HH:mm')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/pandits/${pandit.id}`)
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition-colors duration-150"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditPandit(pandit)
                          }}
                          className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-50 transition-colors duration-150"
                          title="Edit Pandit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <div className="relative group">
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-50 transition-colors duration-150"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                            <div className="py-1">
                              {!pandit.isVerified && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleVerifyPandit(pandit.id)
                                  }}
                                  disabled={verifyingPandits.has(pandit.id)}
                                  className="block w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                >
                                  {verifyingPandits.has(pandit.id) && (
                                    <div className="animate-spin rounded-full h-3 w-3 border-b border-green-700 mr-2"></div>
                                  )}
                                  {verifyingPandits.has(pandit.id) ? 'Verifying...' : 'Verify Pandit'}
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleFeaturePandit(pandit.id, !pandit.featuredPandit)
                                }}
                                disabled={featuringPandits.has(pandit.id)}
                                className="block w-full text-left px-4 py-2 text-sm text-yellow-700 hover:bg-yellow-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                              >
                                {featuringPandits.has(pandit.id) && (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b border-yellow-700 mr-2"></div>
                                )}
                                {featuringPandits.has(pandit.id)
                                  ? 'Updating...'
                                  : (pandit.featuredPandit ? 'Remove from Featured' : 'Make Featured')
                                }
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(`/pandits/${pandit.id}`)
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                              >
                                View Profile
                              </button>
                              <button
                                onClick={(e) => e.stopPropagation()}
                                className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                              >
                                Suspend Pandit
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredPandits.length > 0 && pagination.totalPages > 1 && (
          <div className="bg-white px-6 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={!pagination.hasPrev}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!pagination.hasNext}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">
                      {(pagination.page - 1) * pagination.limit + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">{pagination.total}</span>{' '}
                    results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={!pagination.hasPrev}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>

                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                      .filter(page => page === 1 || page === pagination.totalPages || Math.abs(page - currentPage) <= 1)
                      .map((page, index, array) => (
                        <React.Fragment key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>
                          )}
                          <button
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === page
                                ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}

                    <button
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={!pagination.hasNext}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Pandit Modal */}
      <AddEditPanditModal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        onSave={handleSavePandit}
        pandit={editingPandit}
        services={services}
        loading={modalLoading}
      />
    </div>
  )
}

export default Pandits
